import { el } from '../ui/dom';
import {
  CmsClient,
  type CmsAuditEvent,
  type ImportedCredential,
  type CmsSchool,
  type CmsSystemAdmin,
} from './client';
import { createLoginMethodControl } from './loginMethodControl';
import {
  button,
  dateTime,
  emptyState,
  errorMessage,
  formValue,
  input,
  openDialog,
  selectField,
  statusBadge,
} from './ui';

function openSystemAdminForm(
  client: CmsClient,
  admin: CmsSystemAdmin | null,
  onSaved: () => void,
  announce: (message: string, error?: boolean) => void
): void {
  const modal = openDialog(
    admin ? 'Edit System Admin' : 'Create System Admin',
    'System Admins have cross-school access. They sign in with email and password. Only System Admins can change passwords.'
  );
  const loginControl = createLoginMethodControl({
    current: 'email_password',
  });
  const passwordField = input(admin ? 'New password (leave blank to keep)' : 'Password (leave blank to generate)', 'password', 'password', '');
  passwordField.querySelector('input')?.setAttribute('minlength', '12');
  passwordField.querySelector('input')?.setAttribute('maxlength', '128');
  passwordField.querySelector('input')?.setAttribute('autocomplete', 'new-password');
  const form = el(
    'form',
    { class: 'cms-form' },
    input('Display name', 'displayName', 'text', admin?.displayName ?? '', true),
    input('Sign-in email', 'email', 'email', admin?.email ?? '', true),
    loginControl.element,
    passwordField,
    selectField(
      'Status',
      'status',
      [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
      admin?.status ?? 'active'
    )
  );
  const save = button(admin ? 'Save changes' : 'Create System Admin', 'primary');
  const submit = (): void => {
    if (!form.reportValidity()) return;
    save.disabled = true;
    const loginMethod = loginControl.value();
    const inputValue = {
      displayName: formValue(form, 'displayName'),
      email: formValue(form, 'email'),
      loginMethod,
      ...(formValue(form, 'password') ? { password: formValue(form, 'password') } : {}),
      status: formValue(form, 'status') as 'active' | 'inactive',
    };
    const request = admin
      ? client.updateSystemAdmin(admin.id, inputValue)
      : client.createSystemAdmin(inputValue);
    void request
      .then((result) => {
        if (result.temporaryPassword) {
          modal.body.replaceChildren(
            el('p', { text: 'Save this password securely. It is not included in account listings.' }),
            el('strong', { text: result.email }),
            el('code', { text: result.temporaryPassword }),
            el('div', { class: 'cms-dialog__actions cms-dialog__actions--end' }, button('Done', 'primary', modal.close))
          );
        } else modal.close();
        announce(admin ? 'System Admin updated.' : 'System Admin created with email and password.');
        onSaved();
      })
      .catch((reason) => announce(errorMessage(reason), true))
      .finally(() => {
        save.disabled = false;
      });
  };
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    submit();
  });
  save.addEventListener('click', submit);
  modal.body.append(
    form,
    el(
      'div',
      { class: 'cms-dialog__actions cms-dialog__actions--end' },
      button('Cancel', 'secondary', modal.close),
      save
    )
  );
}

export async function createSystemAdminsView(
  client: CmsClient,
  currentAdminId: string,
  announce: (message: string, error?: boolean) => void
): Promise<HTMLElement> {
  let admins: readonly CmsSystemAdmin[] = [];
  const body = el('tbody');
  const search = el('input', {
    class: 'cms-input',
    attrs: {
      type: 'search',
      placeholder: 'Search System Admins',
      'aria-label': 'Search System Admins',
    },
  });
  const status = el('select', { class: 'cms-select' });
  status.append(
    el('option', { text: 'All statuses', attrs: { value: '' } }),
    el('option', { text: 'Active', attrs: { value: 'active' } }),
    el('option', { text: 'Inactive', attrs: { value: 'inactive' } })
  );

  const render = (): void => {
    const query = search.value.trim().toLowerCase();
    const visible = admins.filter(
      (admin) =>
        (!query || `${admin.displayName} ${admin.email}`.toLowerCase().includes(query)) &&
        (!status.value || admin.status === status.value)
    );
    body.replaceChildren(
      ...visible.map((admin) => {
        const current = admin.id === currentAdminId;
        const toggle = button(
          admin.status === 'active' ? 'Disable' : 'Enable',
          admin.status === 'active' ? 'danger' : 'primary',
          () => {
            void client
              .updateSystemAdmin(admin.id, {
                status: admin.status === 'active' ? 'inactive' : 'active',
              })
              .then(() => {
                announce(`System Admin ${admin.status === 'active' ? 'disabled' : 'enabled'}.`);
                return refresh();
              })
              .catch((reason) => announce(errorMessage(reason), true));
          }
        );
        if (current) {
          toggle.disabled = true;
          toggle.title = 'You cannot disable your own account.';
        }
        return el(
          'tr',
          {},
          el(
            'td',
            {},
            el('strong', { text: admin.displayName }),
            el('span', { class: 'cms-row-subtitle', text: admin.email }),
            ...(current ? [statusBadge('current account')] : [])
          ),
          el(
            'td',
            {},
            el(
              'div',
              { class: 'cms-provider-state' },
              statusBadge('email & password')
            )
          ),
          el('td', { text: dateTime(admin.firstLoginAt) }),
          el('td', { text: dateTime(admin.lastLoginAt) }),
          el('td', {}, statusBadge(admin.status)),
          el(
            'td',
            {},
            el(
              'div',
              { class: 'cms-row-actions' },
              button('Edit', 'quiet', () =>
                openSystemAdminForm(client, admin, () => void refresh(), announce)
              ),
              toggle
            )
          )
        );
      })
    );
    if (!visible.length) {
      body.replaceChildren(
        el(
          'tr',
          {},
          el(
            'td',
            { attrs: { colspan: 6 } },
            emptyState('No System Admins found.', 'Change the filters or create an account.')
          )
        )
      );
    }
  };

  const refresh = async (): Promise<void> => {
    try {
      admins = await client.systemAdmins();
      render();
      announce(`${admins.length} System Admins loaded.`);
    } catch (reason) {
      announce(errorMessage(reason), true);
      body.replaceChildren(
        el(
          'tr',
          {},
          el(
            'td',
            { attrs: { colspan: 6 } },
            emptyState('System Admins unavailable.', errorMessage(reason))
          )
        )
      );
    }
  };
  search.addEventListener('input', render);
  status.addEventListener('change', render);

  const view = el(
    'div',
    { class: 'cms-view' },
    el(
      'div',
      { class: 'cms-view-heading' },
      el(
        'div',
        {},
        el('h1', { text: 'System Admins' }),
        el('p', {
          text: 'Create cross-school administrators and manage their email-and-password access.',
        })
      ),
      button('Create System Admin', 'primary', () =>
        openSystemAdminForm(client, null, () => void refresh(), announce)
      )
    ),
    el(
      'div',
      { class: 'cms-filter-bar' },
      el('label', { class: 'cms-search-field' }, search),
      status,
      button('Refresh', 'secondary', () => void refresh())
    ),
    el(
      'section',
      { class: 'cms-panel cms-panel--flush' },
      el(
        'div',
        { class: 'cms-table-scroll' },
        el(
          'table',
          { class: 'cms-data-table' },
          el(
            'thead',
            {},
            el(
              'tr',
              {},
              ...['System Admin', 'Login', 'First login', 'Last login', 'Status', 'Actions'].map(
                (label) => el('th', { text: label })
              )
            )
          ),
          body
        )
      )
    )
  );
  await refresh();
  return view;
}

interface RangeControl {
  readonly element: HTMLElement;
  readonly input: HTMLInputElement;
}

function rangeControl(
  label: string,
  name: string,
  minimum: number,
  maximum: number,
  value: number,
  hint: string
): RangeControl {
  const control = el('input', {
    class: 'cms-setup-range',
    attrs: {
      type: 'range',
      name,
      min: minimum,
      max: maximum,
      value,
      step: 1,
      'aria-label': label,
    },
  });
  const output = el('output', {
    class: 'cms-setup-range__value',
    attrs: { for: name },
    text: value,
  });
  control.addEventListener('input', () => {
    output.textContent = control.value;
  });
  return {
    input: control,
    element: el(
      'label',
      { class: 'cms-setup-range-field' },
      el('span', { class: 'cms-setup-range__heading' }, el('strong', { text: label }), output),
      control,
      el('small', { text: hint })
    ),
  };
}

function setupCredentialTable(credentials: readonly ImportedCredential[]): HTMLElement {
  return el(
    'div',
    { class: 'cms-table-scroll cms-setup-credentials' },
    el(
      'table',
      { class: 'cms-data-table' },
      el(
        'thead',
        {},
        el(
          'tr',
          {},
          ...['Role', 'Account', 'Password'].map((label) => el('th', { text: label }))
        )
      ),
      el(
        'tbody',
        {},
        ...credentials.map((credential) =>
          el(
            'tr',
            {},
            el('td', {}, statusBadge(credential.role)),
            el('td', {}, el('strong', { text: credential.loginName })),
            el('td', {}, el('code', { text: credential.temporaryPassword }))
          )
        )
      )
    )
  );
}

function openSchoolForm(
  client: CmsClient,
  onSaved: () => void,
  announce: (message: string, error?: boolean) => void
): void {
  const modal = openDialog(
    'Create school organization',
    'Set the domain and account quantities once. The Academy will create groups and secure logins for you.',
    true
  );
  const schoolName = input('School name', 'name', 'text', '', true);
  const domain = input('School email domain', 'domain', 'text', '', true);
  schoolName.querySelector('input')?.setAttribute('placeholder', 'Example Primary School');
  domain.querySelector('input')?.setAttribute('placeholder', 'school.edu.hk');
  const groups = rangeControl('Number of groups', 'groupCount', 1, 12, 3, 'Creates Group 1, Group 2, and so on.');
  const teachers = rangeControl('Number of teachers', 'teacherCount', 1, 20, 2, 'Generates t1@domain, t2@domain, and so on.');
  const students = rangeControl('Students per group', 'studentsPerGroup', 1, 40, 10, 'Generates s1@domain onward and distributes them evenly.');
  const plan = el('section', { class: 'cms-setup-plan', attrs: { 'aria-live': 'polite' } });
  const renderPlan = (): void => {
    const groupCount = Number(groups.input.value);
    const teacherCount = Number(teachers.input.value);
    const studentsPerGroup = Number(students.input.value);
    const totalStudents = groupCount * studentsPerGroup;
    const schoolDomain = (domain.querySelector('input')?.value || 'school.edu.hk')
      .replace(/^@/, '')
      .toLowerCase();
    plan.replaceChildren(
      el(
        'div',
        {},
        el('span', { text: 'Accounts to create' }),
        el('strong', { text: `${teacherCount + totalStudents}` }),
        el('small', { text: `${teacherCount} teachers · ${totalStudents} students` })
      ),
      el(
        'div',
        {},
        el('span', { text: 'Generated logins' }),
        el('strong', { text: `t1@${schoolDomain}` }),
        el('small', { text: `Students start at s1@${schoolDomain}` })
      ),
      el(
        'div',
        {},
        el('span', { text: 'Group structure' }),
        el('strong', { text: `${groupCount} groups` }),
        el('small', { text: `${studentsPerGroup} students in each group` })
      )
    );
  };
  [groups.input, teachers.input, students.input, domain.querySelector('input')].forEach((control) =>
    control?.addEventListener('input', renderPlan)
  );
  renderPlan();
  const form = el(
    'form',
    { class: 'cms-school-setup' },
    el(
      'section',
      { class: 'cms-setup-section' },
      el('div', { class: 'cms-setup-section__heading' }, el('strong', { text: 'Organization' }), el('span', { text: 'School identity and sign-in domain' })),
      el(
        'div',
        { class: 'cms-form cms-setup-form-grid' },
        schoolName,
        domain,
        selectField(
          'Default student language',
          'preferredLanguage',
          [
            { value: 'en', label: 'English' },
            { value: 'zh-HK', label: '繁體中文' },
          ],
          'en'
        )
      )
    ),
    el(
      'section',
      { class: 'cms-setup-section' },
      el('div', { class: 'cms-setup-section__heading' }, el('strong', { text: 'Account plan' }), el('span', { text: 'Adjust the sliders before creating the organization' })),
      el('div', { class: 'cms-setup-ranges' }, groups.element, teachers.element, students.element),
      plan
    )
  );
  const save = button('Create organization & accounts', 'primary');
  const submit = (): void => {
    if (!form.reportValidity()) return;
    save.disabled = true;
    save.textContent = 'Generating secure accounts…';
    void client
      .createSchoolSetup({
        name: formValue(form, 'name'),
        domain: formValue(form, 'domain'),
        groupCount: Number(groups.input.value),
        teacherCount: Number(teachers.input.value),
        studentsPerGroup: Number(students.input.value),
        preferredLanguage: formValue(form, 'preferredLanguage') === 'zh-HK' ? 'zh-HK' : 'en',
      })
      .then((result) => {
        client.downloadCsv(result.csv, `${result.school.code.toLowerCase()}-credentials.csv`);
        modal.body.replaceChildren(
          el(
            'div',
            { class: 'cms-success-banner cms-setup-success' },
            el('strong', { text: `${result.school.name} is ready.` }),
            el('p', {
              text: `${result.groups.length} groups and ${result.credentials.length} email-and-password accounts were created. The credential CSV downloaded automatically.`,
            })
          ),
          el(
            'div',
            { class: 'cms-setup-created-groups' },
            ...result.groups.map((group) =>
              el('span', {}, el('strong', { text: group.name }), el('small', { text: `${group.studentCount} students` }))
            )
          ),
          setupCredentialTable(result.credentials),
          el(
            'div',
            { class: 'cms-dialog__actions cms-dialog__actions--end' },
            button('Download credentials again', 'secondary', () =>
              client.downloadCsv(result.csv, `${result.school.code.toLowerCase()}-credentials.csv`)
            ),
            button('Done', 'primary', modal.close)
          )
        );
        announce(`${result.school.name} created with ${result.credentials.length} accounts.`);
        onSaved();
      })
      .catch((reason) => announce(errorMessage(reason), true))
      .finally(() => {
        save.disabled = false;
        save.textContent = 'Create organization & accounts';
      });
  };
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    submit();
  });
  save.addEventListener('click', submit);
  modal.body.append(
    form,
    el(
      'div',
      { class: 'cms-dialog__actions cms-dialog__actions--end' },
      button('Cancel', 'secondary', modal.close),
      save
    )
  );
}

function openDomainForm(
  client: CmsClient,
  school: CmsSchool,
  onSaved: () => void,
  announce: (message: string, error?: boolean) => void
): void {
  const modal = openDialog('Add allowed domain', `Exact verified email domains for ${school.name}.`);
  const form = el('form', { class: 'cms-form' }, input('Email domain', 'domain', 'text', '', true));
  const save = button('Add domain', 'primary');
  modal.body.append(form, el('div', { class: 'cms-dialog__actions cms-dialog__actions--end' }, button('Cancel', 'secondary', modal.close), save));
  save.addEventListener('click', () => {
    save.disabled = true;
    void client
      .addDomain(school.id, formValue(form, 'domain'))
      .then(() => {
        modal.close();
        announce('Allowed domain added.');
        onSaved();
      })
      .catch((reason) => announce(errorMessage(reason), true))
      .finally(() => {
        save.disabled = false;
      });
  });
}

export async function createSchoolsView(
  client: CmsClient,
  onSchoolsChanged: (schools: readonly CmsSchool[]) => void,
  announce: (message: string, error?: boolean) => void
): Promise<HTMLElement> {
  let schools: readonly CmsSchool[] = [];
  const list = el('div', { class: 'cms-school-list' });
  const search = el('input', { class: 'cms-input', attrs: { type: 'search', placeholder: 'Search schools', 'aria-label': 'Search schools' } });

  const render = (): void => {
    const query = search.value.trim().toLowerCase();
    const visible = schools.filter((school) => !query || `${school.code} ${school.name}`.toLowerCase().includes(query));
    list.replaceChildren(
      ...visible.map((school) => {
        const downloadCredentials = button('Download credentials', 'secondary', () => {
          downloadCredentials.disabled = true;
          void client
            .downloadSchoolCredentials(school.id)
            .then(() => announce(`${school.name} credentials downloaded.`))
            .catch((reason) => announce(errorMessage(reason), true))
            .finally(() => {
              downloadCredentials.disabled = false;
            });
        });
        if (!school.hasCredentialExport) {
          downloadCredentials.disabled = true;
          downloadCredentials.title = 'No retained credentials file is available for this organization.';
        }
        return el(
          'article',
          { class: 'cms-school-row' },
          el(
            'header',
            { class: 'cms-school-row__header' },
            el(
              'div',
              { class: 'cms-school-row__identity' },
              el('span', { class: 'cms-school-code', text: school.code }),
              el('div', {}, el('h2', { text: school.name }), el('p', { text: school.id }))
            ),
            el(
              'div',
              { class: 'cms-row-actions' },
              statusBadge(school.status),
              downloadCredentials,
              button('Add domain', 'secondary', () =>
                openDomainForm(client, school, () => void refresh(), announce)
              ),
              button(
                school.status === 'active' ? 'Disable school' : 'Enable school',
                school.status === 'active' ? 'danger' : 'primary',
                () => {
                  void client
                    .updateSchool(school.id, {
                      status: school.status === 'active' ? 'inactive' : 'active',
                    })
                    .then(() => refresh())
                    .catch((reason) => announce(errorMessage(reason), true));
                }
              )
            )
          ),
          el(
            'section',
            { class: 'cms-school-facts', attrs: { 'aria-label': `${school.name} account summary` } },
            ...[
              ['Teachers', school.teacherCount ?? 0],
              ['Students', school.studentCount ?? 0],
              ['Groups', school.groups?.length ?? 0],
            ].map(([label, value]) =>
              el('div', {}, el('span', { text: String(label) }), el('strong', { text: String(value) }))
            ),
            el(
              'div',
              {},
              el('span', { text: 'Login' }),
              el('strong', { text: 'Email & password' })
            )
          ),
          el(
            'section',
            { class: 'cms-school-domain-section' },
            el('h3', { text: 'School domain' }),
            el(
              'div',
              { class: 'cms-domain-list' },
              ...(school.domains?.length
                ? school.domains.map((domain) =>
                    el(
                      'span',
                      { class: 'cms-domain-chip' },
                      domain.domain,
                      statusBadge(domain.status),
                      button(domain.status === 'active' ? 'Disable' : 'Enable', 'quiet', () => {
                        void client
                          .updateDomain(
                            school.id,
                            domain.id,
                            domain.status === 'active' ? 'inactive' : 'active'
                          )
                          .then(() => refresh())
                          .catch((reason) => announce(errorMessage(reason), true));
                      })
                    )
                  )
                : [el('span', { class: 'cms-row-subtitle', text: 'No allowed domains yet' })])
            )
          ),
          el(
            'section',
            { class: 'cms-school-group-section' },
            el('h3', { text: 'Groups' }),
            el(
              'div',
              { class: 'cms-school-group-grid' },
              ...(school.groups?.length
                ? school.groups.map((group) =>
                    el(
                      'div',
                      {},
                      el('strong', { text: group.name }),
                      el('span', { text: `${group.studentCount} students` })
                    )
                  )
                : [el('span', { class: 'cms-row-subtitle', text: 'No groups created yet' })])
            )
          )
        );
      })
    );
    if (!visible.length) list.replaceChildren(emptyState('No schools found.', 'Create a school or change the search.'));
  };

  const refresh = async (): Promise<void> => {
    try {
      schools = await client.schools();
      render();
      onSchoolsChanged(schools);
      announce(`${schools.length} schools loaded.`);
    } catch (reason) {
      announce(errorMessage(reason), true);
      list.replaceChildren(emptyState('Schools unavailable.', errorMessage(reason)));
    }
  };
  search.addEventListener('input', render);
  const view = el(
    'div',
    { class: 'cms-view' },
    el('div', { class: 'cms-view-heading' }, el('div', {}, el('h1', { text: 'Schools' }), el('p', { text: 'Create an organization, groups and ready-to-use school accounts in one setup.' })), button('Create organization', 'primary', () => openSchoolForm(client, () => void refresh(), announce))),
    el('div', { class: 'cms-filter-bar' }, el('label', { class: 'cms-search-field' }, search), button('Refresh', 'secondary', () => void refresh())),
    list
  );
  await refresh();
  return view;
}

function auditMetadata(event: CmsAuditEvent): string {
  return Object.entries(event.metadata)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(' · ');
}

export async function createAuditView(
  client: CmsClient,
  schoolId: string,
  announce: (message: string, error?: boolean) => void
): Promise<HTMLElement> {
  let events: readonly CmsAuditEvent[] = [];
  const body = el('tbody');
  const search = el('input', { class: 'cms-input', attrs: { type: 'search', placeholder: 'Search action, actor or entity', 'aria-label': 'Search audit events' } });
  const action = el('select', { class: 'cms-select' });
  const from = el('input', { class: 'cms-input', attrs: { type: 'date', 'aria-label': 'Audit from date' } });
  const to = el('input', { class: 'cms-input', attrs: { type: 'date', 'aria-label': 'Audit to date' } });

  const render = (): void => {
    const query = search.value.trim().toLowerCase();
    const fromTime = from.value ? new Date(`${from.value}T00:00:00`).getTime() : 0;
    const toTime = to.value ? new Date(`${to.value}T23:59:59.999`).getTime() : Number.POSITIVE_INFINITY;
    const rows = events.filter((event) => {
      const time = Date.parse(event.occurredAt);
      return (!query || `${event.action} ${event.actorAccountId} ${event.entityType} ${event.entityId} ${auditMetadata(event)}`.toLowerCase().includes(query)) && (!action.value || event.action === action.value) && time >= fromTime && time <= toTime;
    });
    body.replaceChildren(...rows.map((event) => el('tr', {}, el('td', { text: dateTime(event.occurredAt) }), el('td', {}, el('strong', { text: event.action }), el('span', { class: 'cms-row-subtitle', text: auditMetadata(event) })), el('td', {}, statusBadge(event.actorRole)), el('td', { text: event.actorAccountId }), el('td', {}, el('strong', { text: event.entityType }), el('span', { class: 'cms-row-subtitle', text: event.entityId })) )));
    if (!rows.length) body.replaceChildren(el('tr', {}, el('td', { attrs: { colspan: 5 } }, emptyState('No audit events found.', 'Change the filters or perform a CMS action.'))));
  };

  const refresh = async (): Promise<void> => {
    try {
      events = await client.audits(schoolId || undefined);
      const current = action.value;
      action.replaceChildren(el('option', { text: 'All actions', attrs: { value: '' } }), ...[...new Set(events.map((event) => event.action))].sort().map((value) => el('option', { text: value, attrs: { value } })));
      action.value = current;
      render();
      announce(`${events.length} audit events loaded.`);
    } catch (reason) {
      announce(errorMessage(reason), true);
    }
  };
  search.addEventListener('input', render);
  action.addEventListener('change', render);
  from.addEventListener('change', render);
  to.addEventListener('change', render);
  const view = el('div', { class: 'cms-view' }, el('div', { class: 'cms-view-heading' }, el('div', {}, el('h1', { text: 'Audit' }), el('p', { text: 'Authentication, domain, import, disable, reset and report-export activity.' }))), el('div', { class: 'cms-filter-bar' }, el('label', { class: 'cms-search-field' }, search), action, el('label', { class: 'cms-date-field' }, el('span', { text: 'From' }), from), el('label', { class: 'cms-date-field' }, el('span', { text: 'To' }), to), button('Refresh', 'secondary', () => void refresh())), el('section', { class: 'cms-panel cms-panel--flush' }, el('div', { class: 'cms-table-scroll' }, el('table', { class: 'cms-data-table' }, el('thead', {}, el('tr', {}, ...['When', 'Action', 'Role', 'Actor', 'Target'].map((label) => el('th', { text: label })))), body))));
  await refresh();
  return view;
}
